import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalUser } from '../../contexts/GlobalUsersContext';
import {
  GranteeType,
  PermissionRole,
  UserPermissions,
} from '../../api/userPermissions';
import DropdownMenu, { DropdownMenuItem } from '../DropdownMenu/DropdownMenu';
import { getCurrentUserID } from '../../utils/userUtils';
import { getCurrentUserInfo } from '../../utils/userUtils';
import { getUsers, getUsersByIds } from '../../api/user';
import './PermissionUserSearch.css';

type StoredPermissionRole = Exclude<PermissionRole, PermissionRole.OWNER>;
type SearchScope = 'internal' | 'external';
const REMOVE_ROLE_MENU_ID = '__remove__';

export interface PermissionUserSearchProps {
  value?: string | null;
  ownerID?: string | null;
  permissions?: UserPermissions[];
  onChange: (userID: string | null, role?: StoredPermissionRole) => void;
  onRoleChange?: (permission: UserPermissions, role: StoredPermissionRole) => void;
  onRemove?: (permission: UserPermissions) => void;
  onTransferOwnership?: (
    userID: string,
    previousOwnerRole: StoredPermissionRole | 'remove',
    removeAllCollaborators: boolean,
  ) => void;
  disabled?: boolean;
  visible: boolean;
  onClose: () => void;
}

interface Collaborator {
  userID: string;
  user?: GlobalUser;
  role: PermissionRole;
  permission?: UserPermissions;
}

interface RoleMenuState {
  kind: 'collaborator' | 'search' | 'confirm';
  id: string;
  top: number;
  left: number;
}

const ROLE_ORDER: Record<PermissionRole, number> = {
  [PermissionRole.OWNER]: 0,
  [PermissionRole.ADMIN]: 1,
  [PermissionRole.EDITOR]: 2,
  [PermissionRole.COPY]: 3,
  [PermissionRole.VIEWER]: 4,
};

function getUserInitial(user: Pick<GlobalUser, 'userNickName' | 'userName'> | null): string {
  if (!user) return 'U';
  const name = user.userNickName || user.userName || '';
  if (!name) return 'U';
  return /[\u4e00-\u9fa5]/.test(name) ? name.charAt(0) : name.charAt(0).toUpperCase();
}

function getUserDisplayName(user?: GlobalUser, fallbackID?: string): string {
  if (!user) return fallbackID || '';
  return user.userNickName || user.userName || user.userEmail || user.userPhone || user.userID;
}

const PermissionUserSearch: React.FC<PermissionUserSearchProps> = ({
  ownerID,
  permissions = [],
  onChange,
  onRoleChange,
  onRemove,
  onTransferOwnership,
  disabled,
  visible,
  onClose,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [searchScope, setSearchScope] = useState<SearchScope>('internal');
  const [externalSearchText, setExternalSearchText] = useState('');
  const [removeAllCollaborators, setRemoveAllCollaborators] = useState(false);
  const [pendingOwnerID, setPendingOwnerID] = useState<string | null>(null);
  const [previousOwnerRole, setPreviousOwnerRole] = useState<StoredPermissionRole | 'remove'>(PermissionRole.ADMIN);
  const [searchResults, setSearchResults] = useState<GlobalUser[]>([]);
  const [usersById, setUsersById] = useState<Map<string, GlobalUser>>(new Map());
  const [roleOverrides, setRoleOverrides] = useState<Record<string, StoredPermissionRole>>({});
  const [roleMenu, setRoleMenu] = useState<RoleMenuState | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const collaborators = useMemo<Collaborator[]>(() => {
    const byUserID = new Map<string, Collaborator>();
    if (ownerID) {
      byUserID.set(ownerID, {
        userID: ownerID,
        user: usersById.get(ownerID),
        role: PermissionRole.OWNER,
      });
    }
    permissions.forEach((permission) => {
      if (permission.granteeType !== GranteeType.USER || !permission.granteeID) return;
      if (permission.granteeID === ownerID) return;
      byUserID.set(permission.granteeID, {
        userID: permission.granteeID,
        user: usersById.get(permission.granteeID),
        role: roleOverrides[permission.permissionID] || permission.role,
        permission,
      });
    });
    return Array.from(byUserID.values()).sort((a, b) => {
      const roleDiff = ROLE_ORDER[a.role] - ROLE_ORDER[b.role];
      if (roleDiff !== 0) return roleDiff;
      return getUserDisplayName(a.user, a.userID).localeCompare(
        getUserDisplayName(b.user, b.userID),
        'zh-CN'
      );
    });
  }, [ownerID, permissions, roleOverrides, usersById]);

  const collaboratorIDs = useMemo(
    () => new Set(collaborators.map((collaborator) => collaborator.userID)),
    [collaborators]
  );

  const roleMenuItems = useMemo<DropdownMenuItem[]>(() => [
    { id: PermissionRole.ADMIN, label: t('permissionUserSearch.canManage') },
    { id: PermissionRole.EDITOR, label: t('permissionUserSearch.canEdit') },
  ], [t]);

  const getRoleLabel = useCallback((role?: PermissionRole) => {
    if (role === PermissionRole.OWNER) return t('permissionUserSearch.owner');
    if (role === PermissionRole.ADMIN) return t('permissionUserSearch.canManage');
    if (role === PermissionRole.EDITOR) return t('permissionUserSearch.canEdit');
    if (role === PermissionRole.COPY) return t('permissionUserSearch.canCopy');
    if (role === PermissionRole.VIEWER) return t('permissionUserSearch.viewOnly');
    return t('permissionUserSearch.selectPermission');
  }, [t]);

  useEffect(() => {
    if (visible) {
      setSearchText('');
      setSearchScope('internal');
      setExternalSearchText('');
      setRemoveAllCollaborators(false);
      setPendingOwnerID(null);
      setPreviousOwnerRole(PermissionRole.ADMIN);
      setSearchResults([]);
      setRoleOverrides({});
      setRoleMenu(null);

      let cancelled = false;
      const collaboratorUserIds = Array.from(new Set([
        ...(ownerID ? [ownerID] : []),
        ...permissions
          .filter(permission => permission.granteeType === GranteeType.USER)
          .map(permission => permission.granteeID)
          .filter(Boolean),
      ]));
      if (collaboratorUserIds.length > 0) {
        void getUsersByIds(collaboratorUserIds)
          .then(response => {
            if (!cancelled) setUsersById(new Map((response.data || []).map(user => [user.userID, user])));
          })
          .catch(error => console.warn('加载当前协作者信息失败:', error));
      }
      return () => {
        cancelled = true;
      };
    }
  }, [ownerID, permissions, visible]);

  const handleSearch = useCallback(
    (text: string) => {
      setSearchText(text);
      const trimmed = text.trim();
      if (!trimmed) {
        setSearchResults([]);
        return;
      }
      if (searchScope === 'external') setSearchResults([]);
    },
    [searchScope]
  );

  useEffect(() => {
    const keyword = (searchScope === 'internal' ? searchText : externalSearchText).trim();
    if (!visible || !keyword) return;
    if (searchScope === 'external' && searchText.trim() !== externalSearchText) return;
    let cancelled = false;
    const runSearch = () => {
      let userOrgID: string | undefined;
      if (searchScope === 'internal') {
        try { userOrgID = getCurrentUserInfo().userOrgID; } catch { /* ignore */ }
      }
      void getUsers({
        keyword,
        userOrgID,
        limit: 20,
        exact: searchScope === 'external' ? 'true' : 'false',
      })
        .then(response => {
          if (cancelled) return;
          const currentUserID = getCurrentUserID();
          setSearchResults((response.data || []).filter(user =>
            user.userID !== currentUserID && !collaboratorIDs.has(user.userID)
          ));
        })
        .catch(error => {
          if (!cancelled) console.warn('搜索可授权用户失败:', error);
        });
    };
    const timer = window.setTimeout(runSearch, searchScope === 'internal' ? 300 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [collaboratorIDs, externalSearchText, searchScope, searchText, visible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('.dropdown-menu')) return;
      if (searchRef.current && !searchRef.current.contains(target)) onClose();
    };
    if (visible) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  const handleCollaboratorRoleChange = (collaborator: Collaborator, role: StoredPermissionRole) => {
    if (!collaborator.permission || disabled) return;
    setRoleOverrides((previous) => ({ ...previous, [collaborator.permission!.permissionID]: role }));
    onRoleChange?.(collaborator.permission, role);
  };

  const handleSearchRoleChange = (userID: string, role: string) => {
    if (!role || disabled) return;
    onChange(userID, role as StoredPermissionRole);
    setSearchText('');
    setSearchResults([]);
  };

  const openRoleMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    kind: RoleMenuState['kind'],
    id: string
  ) => {
    if (disabled) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setRoleMenu((current) => current?.kind === kind && current.id === id ? null : {
      kind,
      id,
      top: rect.bottom + 2,
      left: rect.right - 94,
    });
  };

  if (!visible) return null;
  const trimmedSearch = searchText.trim();
  const hasActiveSearch = searchScope === 'internal' ? Boolean(trimmedSearch) : Boolean(externalSearchText);

  if (pendingOwnerID) {
    return (
      <div className="permission-user-search" ref={searchRef}>
        <div className="permission-user-search-dialog permission-user-search-confirm-dialog">
          <div className="permission-user-search-confirm-message">
            {t('permissionUserSearch.transferOwnerWarning')}
          </div>
          <label className="permission-user-search-confirm-role">
            <span>{t('permissionUserSearch.adjustMeTo')}</span>
            <button
              type="button"
              className="permission-user-search-role-button"
              onClick={(event) => openRoleMenu(event, 'confirm', 'previous-owner-role')}
            >
              {previousOwnerRole === 'remove'
                ? t('permissionUserSearch.removeFromCollaborators')
                : getRoleLabel(previousOwnerRole)}
            </button>
            {roleMenu?.kind === 'confirm' && (
              <DropdownMenu
                items={roleMenuItems}
                visible
                onClose={() => setRoleMenu(null)}
                onSelect={(item) => {
                  setPreviousOwnerRole(item.id as StoredPermissionRole | 'remove');
                  setRoleMenu(null);
                }}
                selectedValue={previousOwnerRole}
                position={{ top: roleMenu.top, left: roleMenu.left }}
                width={94}
                useFixedPosition
                portalToBody
                className="permission-user-search-role-menu"
              />
            )}
          </label>
          <label className="permission-user-search-confirm-checkbox">
            <input
              type="checkbox"
              checked={removeAllCollaborators}
              onChange={(event) => setRemoveAllCollaborators(event.target.checked)}
            />
            <span>{t('permissionUserSearch.removeAllCollaborators')}</span>
          </label>
          <div className="permission-user-search-confirm-actions">
            <button type="button" onClick={() => setPendingOwnerID(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="is-primary"
              onClick={() => onTransferOwnership?.(pendingOwnerID, previousOwnerRole, removeAllCollaborators)}
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderAvatar = (user?: GlobalUser) => (
    <div className="permission-user-search-avatar-initial">{getUserInitial(user || null)}</div>
  );

  return (
    <div className="permission-user-search" ref={searchRef}>
      <div className="permission-user-search-dialog">
        <section className="permission-user-search-collaborators">
          <div className="permission-user-search-section-title">
            {t('permissionUserSearch.allCollaborators')}
          </div>
          <div className="permission-user-search-collaborator-list">
            {collaborators.length ? collaborators.map((collaborator) => (
              <div className="permission-user-search-person-row" key={collaborator.userID}>
                <div className="permission-user-search-person">
                  {renderAvatar(collaborator.user)}
                  <span className="permission-user-search-user-name">
                    {getUserDisplayName(collaborator.user, collaborator.userID)}
                  </span>
                </div>
                <div className="permission-user-search-permission-actions">
                {collaborator.role === PermissionRole.OWNER ? (
                  <button type="button" className="permission-user-search-role-button" disabled>
                    {getRoleLabel(PermissionRole.OWNER)}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="permission-user-search-role-button"
                    disabled={disabled}
                    onClick={(event) => openRoleMenu(event, 'collaborator', collaborator.userID)}
                  >
                    {getRoleLabel(collaborator.role)}
                  </button>
                )}
                {roleMenu?.kind === 'collaborator' && roleMenu.id === collaborator.userID && (
                  <DropdownMenu
                    items={onRemove && collaborator.permission
                      ? [
                          ...roleMenuItems,
                          ...(onTransferOwnership ? [{ id: PermissionRole.OWNER, label: t('permissionUserSearch.owner') }] : []),
                          { id: '__remove_divider__', label: '', divider: true },
                          { id: REMOVE_ROLE_MENU_ID, label: t('permissionUserSearch.remove') },
                        ]
                      : roleMenuItems}
                    visible
                    onClose={() => setRoleMenu(null)}
                    onSelect={(item) => {
                      if (item.id === REMOVE_ROLE_MENU_ID && collaborator.permission) {
                        onRemove?.(collaborator.permission);
                      } else if (item.id === PermissionRole.OWNER) {
                        setPendingOwnerID(collaborator.userID);
                      } else {
                        handleCollaboratorRoleChange(collaborator, item.id as StoredPermissionRole);
                      }
                      setRoleMenu(null);
                    }}
                    selectedValue={collaborator.role}
                    position={{ top: roleMenu.top, left: roleMenu.left }}
                    width={94}
                    useFixedPosition
                    portalToBody
                    className="permission-user-search-role-menu"
                  />
                )}
                </div>
              </div>
            )) : (
              <div className="permission-user-search-empty">{t('permissionUserSearch.noCollaborators')}</div>
            )}
          </div>
        </section>

        <div className="permission-user-search-add-header">
          <div className="permission-user-search-section-title">
            {t('permissionUserSearch.addCollaborator')}
          </div>
          <label className={`permission-user-search-scope-toggle permission-user-search-scope-toggle--${searchScope}`}>
            <input
              type="checkbox"
              role="switch"
              checked={searchScope === 'external'}
              onChange={(event) => {
                const nextScope: SearchScope = event.target.checked ? 'external' : 'internal';
                setSearchScope(nextScope);
                setSearchText('');
                setExternalSearchText('');
                setSearchResults([]);
              }}
            />
            <span className="permission-user-search-scope-track" aria-hidden="true">
              <span className="permission-user-search-scope-indicator" />
              <span>{t('permissionUserSearch.organizationInternal')}</span>
              <span>{t('permissionUserSearch.organizationExternal')}</span>
            </span>
          </label>
        </div>

        <div className="permission-user-search-input">
          <input
            type="text"
            value={searchText}
            onChange={(event) => handleSearch(event.target.value)}
            onKeyDown={(event) => {
              if (searchScope === 'external' && event.key === 'Enter') {
                event.preventDefault();
                setExternalSearchText(searchText.trim());
              }
            }}
            placeholder={searchScope === 'internal'
              ? t('permissionUserSearch.placeholder')
              : t('permissionUserSearch.externalPlaceholder')}
            autoFocus
            disabled={disabled}
          />
        </div>

        <div className="permission-user-search-results">
          {!hasActiveSearch ? (
            <div className="permission-user-search-hint">
              {searchScope === 'internal'
                ? t('permissionUserSearch.searchHint')
                : t('permissionUserSearch.externalSearchHint')}
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map((user) => (
              <div key={user.userID} className="permission-user-search-person-row">
                <div className="permission-user-search-person">
                  {renderAvatar(user)}
                  <span className="permission-user-search-user-name">{getUserDisplayName(user)}</span>
                </div>
                <button
                    type="button"
                    className="permission-user-search-role-button is-empty"
                    disabled={disabled}
                    aria-label={t('permissionUserSearch.selectPermission')}
                    onClick={(event) => openRoleMenu(event, 'search', user.userID)}
                  >
                    {t('permissionUserSearch.selectPermission')}
                  </button>
                {roleMenu?.kind === 'search' && roleMenu.id === user.userID && (
                  <DropdownMenu
                    items={onTransferOwnership
                      ? [...roleMenuItems, { id: PermissionRole.OWNER, label: t('permissionUserSearch.owner') }]
                      : roleMenuItems}
                    visible
                    onClose={() => setRoleMenu(null)}
                    onSelect={(item) => {
                      if (item.id === PermissionRole.OWNER) {
                        setPendingOwnerID(user.userID);
                        setRoleMenu(null);
                      } else {
                        handleSearchRoleChange(user.userID, String(item.id));
                      }
                    }}
                    selectedValue={null}
                    position={{ top: roleMenu.top, left: roleMenu.left }}
                    width={94}
                    useFixedPosition
                    portalToBody
                    className="permission-user-search-role-menu"
                  />
                )}
              </div>
            ))
          ) : (
            <div className="permission-user-search-empty">{t('permissionUserSearch.empty')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionUserSearch;
